import { Product } from "@/features/widgets/components/Comparison/types/ComparisonTypes";

type Listener = (products: Product[]) => void;

const STORAGE_KEY = `ws-comparison-products-${ location.hostname }`;

class ComparisonStore {
  private products = new Map<string, Product>();
  private listeners: Listener[] = [];
  private storageAvailable = false;

  constructor() {
    this.storageAvailable = this.checkStorage();
    this.loadState();
  }

  subscribe(listener: Listener) {
    this.listeners.push(listener);
    listener(this.getProducts());

    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  has(url: string) {
    url = this.stripQueryParams(url);
    return this.products.has(url);
  }

  toggle(product: Product) {
    product.url = this.stripQueryParams(product.url);
    product.category = product.category ? product.category.toLowerCase() : undefined;

    if (this.products.has(product.url)) {
      this.products.delete(product.url);
    } else {
      this.products.set(product.url, product);
    }

    this.saveState();
    this.notify();
  }

  add(product: Product) {
    product.url = this.stripQueryParams(product.url);
    product.category = product.category ? product.category.toLowerCase() : undefined;

    this.products.set(product.url, product);

    this.saveState();
    this.notify();
  }

  remove(url: string) {
    url = this.stripQueryParams(url);

    this.products.delete(url);

    this.saveState();
    this.notify();
  }

  getProducts() {
    const products = Array.from(this.products.values());
    products.sort((a, b) => a.name.localeCompare(b.name));
    return products;
  }

  private notify() {
    const data = this.getProducts();
    this.listeners.forEach(listener => listener(data));
  }

  private checkStorage(): boolean {
    try {
      const testKey = `__ws_test__`;
      localStorage.setItem(testKey, `1`);
      localStorage.removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  }

  private saveState() {
    if (!this.storageAvailable) {
      return;
    }

    const products = this.getProducts();
    try {
      const data = JSON.stringify(products);
      localStorage.setItem(STORAGE_KEY, data);
    } catch {
      console.warn(`Can not persist comparison products (is private mode on?).`);
    }
  }

  private loadState() {
    if (!this.storageAvailable) {
      return;
    }

    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        return;
      }

      const parsed: Product[] = JSON.parse(stored);
      parsed.forEach(product => {
        this.products.set(product.url, product);
      });
    } catch {
      console.warn(`Comparison storage seems to be corrupted.`);
    }
  }

  private stripQueryParams(url: string): string {
    try {
      const parsed = new URL(url, location.origin);
      parsed.search = '';
      parsed.hash = '';
      return parsed.href;
    } catch {
      console.warn(`Can not strip query params from URL: ${ url }`);
      return url.split('?')[0];
    }
  }

}

export const comparisonStore = new ComparisonStore();
